import { styled } from "@linaria/react"
import { useEffect, useState } from "react"

import { useQueries } from "@/hooks/useRootContext.ts"
import { ellipsize, smallFontSize } from "@/styles/textStyles.ts"

import type { TID } from "@/lib/client/generated/TypeDefinitions.ts"


type PreviewReadyCountdownProps = {
  previewId: TID
}

export const PreviewReadyCountdown = ({ previewId }: PreviewReadyCountdownProps) => {
  const queries = useQueries()
  const [targetTime, setTargetTime] = useState<number | null>(null)
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null)

  useEffect(() => {
    const subscription = queries.subscribeToPreviewReadyEstimate(previewId).subscribe((estimateSeconds: number) => {
      setTargetTime(Date.now() + (estimateSeconds * 1000))
    })

    return () => subscription.unsubscribe()
  }, [previewId, queries])

  useEffect(() => {
    if (targetTime === null) {
      return
    }

    const updateCountdown = () => {
      const remainingMs = targetTime - Date.now()

      setRemainingSeconds(Math.ceil(remainingMs / 1000))
    }

    updateCountdown()

    const intervalId = setInterval(updateCountdown, 1000)

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        updateCountdown()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      clearInterval(intervalId)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [targetTime])

  if (remainingSeconds === null) {
    return <CountdownText>{ellipsize("Estimating remaining time")}</CountdownText>
  }

  if (remainingSeconds <= 0) {
    return <CountdownText>{ellipsize("This is taking a bit longer than expected")}</CountdownText>
  }

  return (
    <CountdownText>Estimated time remaining: {formatTime(remainingSeconds)}</CountdownText>
  )
}

function formatTime(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`
  }
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}m ${remainingSeconds}s`
}

const CountdownText = styled.span`
  ${smallFontSize};

  font-variant-numeric: tabular-nums;

  color: var(--text-color-40);
  text-shadow: 0 4px 10px var(--background-color-10);
  text-align: center;
`
